import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { apiGet, API_BASE } from '@/lib/api';
import {
  Sparkles, Brain, Mic, Camera, Trophy, Shield, Users, Zap, Star, Crown,
  Check, ArrowRight, BookOpen, Heart, Image as ImageIcon, Quote, Play
} from 'lucide-react';
import MeetYourBuddiesSection from '@/components/MeetYourBuddiesSection';

const BRAND_LOGO = '/brand/logo.png';

// Intersection-observer powered reveal animation
function useReveal() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { rootMargin: '-10% 0px', threshold: 0.15 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return [ref, shown];
}

// 3D mouse-tilt hero brand panel — matches the LIVE page look (logo + wordmark + 3 stats)
function HeroBrandPanel() {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const handleMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const dx = (e.clientX - cx) / r.width;
    const dy = (e.clientY - cy) / r.height;
    // Subtle tilt: ±6°
    setTilt({ x: Math.max(-6, Math.min(6, -dy * 10)), y: Math.max(-6, Math.min(6, dx * 10)) });
  };
  const handleLeave = () => setTilt({ x: 0, y: 0 });

  return (
    <div className="relative [perspective:1200px]" onMouseMove={handleMove} onMouseLeave={handleLeave}>
      {/* Aurora glow behind panel */}
      <div className="absolute -inset-10 bg-gradient-to-br from-cyan-300/30 via-indigo-300/20 to-amber-200/20 blur-3xl rounded-[3rem] animate-aurora" />

      <div ref={ref}
        className="relative rounded-[2.5rem] bg-white/85 backdrop-blur-xl border border-white shadow-[0_30px_90px_-20px_rgba(30,41,59,0.3)] p-8 sm:p-10 transition-transform duration-200 ease-out [transform-style:preserve-3d]"
        style={{ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)` }}>
        {/* Logo — lifted in 3D space with a soft halo ring */}
        <div className="relative flex items-center justify-center mb-5" style={{ transform: 'translateZ(40px)' }}>
          {/* Pulsing halo */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-60 h-60 rounded-full bg-gradient-to-br from-cyan-400/25 via-indigo-400/20 to-amber-300/15 blur-2xl animate-pulse-slow" />
          </div>
          <img src={BRAND_LOGO} alt="Gurukul Veda" className="relative w-64 h-64 sm:w-72 sm:h-72 object-contain drop-shadow-2xl" />
        </div>

        {/* Wordmark */}
        <div className="text-center mb-6" style={{ transform: 'translateZ(20px)' }}>
          <p className="font-heading text-2xl font-bold bg-gradient-to-r from-slate-700 via-slate-500 to-slate-700 bg-clip-text text-transparent tracking-[-0.02em]">Gurukul Veda</p>
        </div>

        {/* Stats cards — each tilts slightly on hover */}
        <div className="grid grid-cols-3 gap-3" style={{ transform: 'translateZ(10px)' }}>
          {[
            { n: '35', label: 'Boards covered' },
            { n: '5,000+', label: 'Chapters' },
            { n: '3,100+', label: 'Schools mapped' },
          ].map((s, i) => (
            <div key={i} className="group p-4 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 text-center hover:shadow-lg hover:-translate-y-0.5 transition-all">
              <p className="font-heading text-xl font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{s.n}</p>
              <p className="text-[10px] text-slate-500 mt-1 font-body">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Floating badges in 3D space */}
        <div className="hidden lg:block absolute -left-6 top-12 bg-white shadow-xl rounded-2xl px-3.5 py-2.5 border border-slate-100 animate-float" style={{ transform: 'translateZ(50px)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center"><Heart className="w-4 h-4 text-emerald-500" /></div>
            <div>
              <p className="font-heading text-xs font-bold text-slate-800">No judgement</p>
              <p className="text-[10px] text-slate-400">Ask the silliest doubt</p>
            </div>
          </div>
        </div>
        <div className="hidden lg:block absolute -right-6 bottom-24 bg-white shadow-xl rounded-2xl px-3.5 py-2.5 border border-slate-100 animate-float-slow" style={{ transform: 'translateZ(50px)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center"><Trophy className="w-4 h-4 text-amber-500" /></div>
            <div>
              <p className="font-heading text-xs font-bold text-slate-800">7-day streak</p>
              <p className="text-[10px] text-slate-400">Habit locked in</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Reveal({ children, delay = 0 }) {
  const [ref, shown] = useReveal();
  return (
    <div ref={ref} className={`transition-all duration-700 ${shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ transitionDelay: shown ? `${delay}ms` : '0ms' }}>
      {children}
    </div>
  );
}

// image_url is "/api/website-ads/{id}/image"; API_BASE already ends in /api
const adImageSrc = (imageUrl) => `${API_BASE}${imageUrl.slice(4)}`;

// ============ WEBSITE ADS — 3D coverflow carousel (admin-managed) ============
// Fetches active ads from /api/website-ads. Renders the very first thing a
// visitor sees (below the ribbon). Auto-rotates, pauses on hover, swipe on
// touch. Renders nothing when the admin hasn't uploaded any ads.
function WebsiteAdsCarousel({ onCta }) {
  const [ads, setAds] = useState([]);
  const [rotationMs, setRotationMs] = useState(1000);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);
  const impressedRef = useRef(false);

  useEffect(() => {
    apiGet('/website-ads').then(d => {
      const list = d.ads || [];
      setAds(list);
      const s = Number(d.rotation_seconds) || 1;
      setRotationMs(Math.max(1, Math.min(10, s)) * 1000);
      // fire exactly one impression beacon per live ad per page view
      if (!impressedRef.current) {
        impressedRef.current = true;
        list.forEach(a => {
          try { fetch(`${API_BASE}/website-ads/${a.id}/impression`, { method: 'POST', keepalive: true }); } catch { /* noop */ }
        });
      }
    }).catch(() => setAds([]));
  }, []);

  const n = ads.length;
  useEffect(() => {
    if (paused || n <= 1) return;
    const t = setInterval(() => setActive(a => (a + 1) % n), rotationMs);
    return () => clearInterval(t);
  }, [paused, n, rotationMs]);

  if (n === 0) return null;

  const go = (dir) => setActive(a => (a + dir + n) % n);
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  const handleClick = (ad) => {
    try {
      const url = `${API_BASE}/website-ads/${ad.id}/click`;
      if (navigator.sendBeacon) navigator.sendBeacon(url);
      else fetch(url, { method: 'POST', keepalive: true });
    } catch { /* noop */ }
    if (ad.link_url) {
      window.open(ad.link_url, ad.link_url.startsWith('http') ? '_blank' : '_self');
    } else if (onCta) {
      onCta();
    }
  };

  return (
    <section
      className="relative overflow-hidden pt-10 sm:pt-14 pb-8 sm:pb-12"
      data-testid="website-ads-carousel"
      aria-roledescription="carousel"
    >
      {/* soft ambient wash */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60rem] h-[30rem] bg-gradient-to-b from-indigo-200/30 via-cyan-100/20 to-transparent blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 text-center">
        <p className="font-heading text-[11px] uppercase tracking-[0.3em] text-indigo-600 mb-2">Why families choose Gurukul Veda</p>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 tracking-[-0.02em] mb-8 sm:mb-10">
          A patient tutor, <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">right there when it matters.</span>
        </h2>

        {/* 3D coverflow stage */}
        <div
          className="relative h-[440px] sm:h-[560px] lg:h-[620px] [perspective:1600px]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="absolute inset-0 flex items-center justify-center [transform-style:preserve-3d]">
            {ads.map((ad, i) => {
              let offset = i - active;
              if (offset > n / 2) offset -= n;
              if (offset < -n / 2) offset += n;
              const abs = Math.abs(offset);
              const isCenter = offset === 0;
              const visible = abs <= 2;
              const translateX = offset * 54;
              const rotateY = offset === 0 ? 0 : (offset > 0 ? -38 : 38);
              const scale = isCenter ? 1 : Math.max(0.7, 0.86 - (abs - 1) * 0.1);
              const z = 100 - abs;
              const opacity = visible ? (isCenter ? 1 : 0.55) : 0;
              return (
                <button
                  key={ad.id}
                  type="button"
                  aria-label={ad.alt_text || `Ad ${i + 1}`}
                  onClick={() => (isCenter ? handleClick(ad) : setActive(i))}
                  data-testid={`ad-slide-${i}`}
                  className="absolute top-1/2 left-1/2 w-[248px] sm:w-[320px] lg:w-[380px] aspect-[2/3] -translate-x-1/2 -translate-y-1/2 rounded-2xl overflow-hidden transition-all duration-[450ms] ease-out will-change-transform focus:outline-none"
                  style={{
                    transform: `translate(-50%, -50%) translateX(${translateX}%) rotateY(${rotateY}deg) scale(${scale})`,
                    zIndex: z,
                    opacity,
                    pointerEvents: visible ? 'auto' : 'none',
                    boxShadow: isCenter
                      ? '0 40px 80px -20px rgba(30,41,59,0.45)'
                      : '0 20px 50px -20px rgba(30,41,59,0.35)',
                  }}
                >
                  <img
                    src={adImageSrc(ad.image_url)}
                    alt={ad.alt_text || 'Gurukul Veda'}
                    className="w-full h-full object-cover bg-white"
                    draggable={false}
                  />
                  {!isCenter && <div className="absolute inset-0 bg-slate-900/25" />}
                </button>
              );
            })}
          </div>

          {n > 1 && (
            <>
              <button
                type="button" onClick={() => go(-1)} aria-label="Previous"
                data-testid="ad-prev"
                className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-[120] w-10 h-10 rounded-full bg-white/90 backdrop-blur border border-slate-200 shadow-lg flex items-center justify-center text-slate-700 hover:bg-white hover:scale-105 transition-all"
              >
                <ArrowRight className="w-4 h-4 rotate-180" />
              </button>
              <button
                type="button" onClick={() => go(1)} aria-label="Next"
                data-testid="ad-next"
                className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-[120] w-10 h-10 rounded-full bg-white/90 backdrop-blur border border-slate-200 shadow-lg flex items-center justify-center text-slate-700 hover:bg-white hover:scale-105 transition-all"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {n > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            {ads.map((ad, i) => (
              <button
                key={ad.id} onClick={() => setActive(i)}
                aria-label={`Go to ad ${i + 1}`}
                data-testid={`ad-dot-${i}`}
                className={`h-1.5 rounded-full transition-all ${i === active ? 'w-9 bg-gradient-to-r from-indigo-500 to-blue-500' : 'w-4 bg-slate-300 hover:bg-slate-400'}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// 30-second animated product tour — 4 scenes auto-cycling
function ProductTour() {
  const [scene, setScene] = useState(0);
  const [paused, setPaused] = useState(false);
  const scenes = [
    { id: 'ask', title: 'Ask a doubt', subtitle: 'Type, snap a photo, or speak' },
    { id: 'reply', title: 'Get a warm reply', subtitle: 'Warm, patient, syllabus-aware' },
    { id: 'snap', title: 'Snap & solve', subtitle: 'Stuck on a worksheet? Photo it.' },
    { id: 'streak', title: 'Unlock a streak', subtitle: 'Small wins, every day' },
  ];
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setScene(s => (s + 1) % scenes.length), 7000);
    return () => clearInterval(t);
  }, [paused, scenes.length]);

  return (
    <section className="py-24 relative" data-testid="product-tour">
      <div className="max-w-5xl mx-auto px-5 sm:px-8">
        <Reveal>
          <div className="text-center mb-12">
            <p className="font-heading text-[11px] uppercase tracking-[0.3em] text-indigo-600 mb-3">Watch how it works</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 leading-[1.05] tracking-[-0.025em]">
              Your child's <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">next 30 seconds</span>, in four scenes.
            </h2>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            className="relative rounded-[2rem] bg-white/85 backdrop-blur-xl border border-slate-900/5 shadow-[0_30px_90px_-20px_rgba(30,41,59,0.25)] p-6 sm:p-10 overflow-hidden"
          >
            {/* Progress dots */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {scenes.map((s, i) => (
                <button key={s.id} onClick={() => setScene(i)} data-testid={`tour-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${i === scene ? 'w-10 bg-gradient-to-r from-indigo-500 to-blue-500' : 'w-5 bg-slate-200 hover:bg-slate-300'}`} />
              ))}
            </div>

            {/* Scene stage */}
            <div className="relative h-[280px] sm:h-[320px] grid place-items-center">
              {/* Scene 1 — ASK */}
              <div className={`absolute inset-0 flex flex-col items-center justify-center transition-all duration-700 ${scene === 0 ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="max-w-sm w-full px-3">
                  <div className="flex justify-end mb-2"><div className="px-3.5 py-2.5 rounded-2xl rounded-br-md bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-sm shadow">What is Newton's third law?</div></div>
                  <div className="flex items-center gap-2 mt-4 justify-center">
                    <div className="flex gap-2 px-4 py-2.5 rounded-full bg-slate-100">
                      <Brain className="w-4 h-4 text-indigo-500" />
                      <span className="text-xs font-heading text-slate-600">Personal Chat</span>
                    </div>
                    <div className="flex gap-2 px-4 py-2.5 rounded-full bg-slate-100">
                      <Camera className="w-4 h-4 text-emerald-500" />
                      <span className="text-xs font-heading text-slate-600">Snap</span>
                    </div>
                    <div className="flex gap-2 px-4 py-2.5 rounded-full bg-slate-100">
                      <Mic className="w-4 h-4 text-sky-500" />
                      <span className="text-xs font-heading text-slate-600">Voice</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scene 2 — WARM REPLY */}
              <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ${scene === 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="max-w-md w-full px-3">
                  <div className="flex items-start gap-2 mb-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-display font-bold text-xs flex-shrink-0">GV</div>
                    <div className="px-3.5 py-3 rounded-2xl rounded-bl-md bg-slate-50 border border-slate-100 text-slate-800 text-sm leading-relaxed">
                      <span className="font-semibold text-indigo-600">Great one!</span> For every action, there's an equal and opposite reaction 🚀 Push a wall — the wall pushes you right back. It's why you can walk, swim, and rockets can fly.
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-10">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-[10px] font-heading border border-amber-100"><Sparkles className="w-3 h-3" /> Aha!</span>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-heading border border-emerald-100"><BookOpen className="w-3 h-3" /> Syllabus-aligned</span>
                  </div>
                </div>
              </div>

              {/* Scene 3 — SNAP & SOLVE */}
              <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ${scene === 2 ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="flex items-center gap-6">
                  {/* Mock worksheet photo */}
                  <div className="w-44 h-56 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 border border-slate-200 shadow-xl p-3 -rotate-3 relative overflow-hidden">
                    <div className="h-1.5 w-3/4 bg-slate-300 rounded mb-2" />
                    <div className="h-1.5 w-full bg-slate-200 rounded mb-1.5" />
                    <div className="h-1.5 w-5/6 bg-slate-200 rounded mb-1.5" />
                    <div className="h-1.5 w-2/3 bg-slate-200 rounded mb-3" />
                    <div className="font-display font-bold text-slate-800 text-sm mb-2">2x + 5 = 17</div>
                    <div className="h-1.5 w-1/2 bg-slate-200 rounded" />
                    <div className="absolute bottom-2 right-2 w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center shadow-lg animate-pulse">
                      <Camera className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  {/* Arrow */}
                  <ArrowRight className="w-6 h-6 text-slate-400 animate-pulse" />
                  {/* Solution card */}
                  <div className="max-w-[200px] p-4 rounded-2xl bg-white border border-slate-100 shadow-xl">
                    <p className="text-[10px] font-heading text-indigo-600 uppercase tracking-wider mb-2">Step-by-step</p>
                    <p className="font-display text-sm font-bold text-slate-900">x = 6</p>
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">Subtract 5 → 2x = 12. Divide both sides by 2 → x = 6.</p>
                  </div>
                </div>
              </div>

              {/* Scene 4 — STREAK */}
              <div className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ${scene === 3 ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                <div className="text-center">
                  <div className="relative inline-flex items-center justify-center w-28 h-28 rounded-full bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500 shadow-2xl mb-5">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 animate-ping opacity-20" />
                    <Trophy className="w-14 h-14 text-white drop-shadow" />
                  </div>
                  <p className="font-display text-3xl font-bold text-slate-900 tracking-tight">7-day streak!</p>
                  <p className="text-sm text-slate-500 mt-1">You're on fire 🔥 Keep going.</p>
                  <div className="flex items-center justify-center gap-2 mt-5">
                    {[1,2,3,4,5,6,7].map(d => (
                      <div key={d} className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white text-[10px] font-heading font-bold shadow">{d}</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Scene label */}
            <div className="mt-6 text-center min-h-[3.5rem]">
              <p className="font-display text-lg font-bold text-slate-900 tracking-tight transition-all duration-500" key={`t-${scene}`}>{scenes[scene].title}</p>
              <p className="text-sm text-slate-500 mt-0.5 transition-all duration-500" key={`s-${scene}`}>{scenes[scene].subtitle}</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    apiGet('/public/settings').then(setSettings).catch(() => setSettings({ feature_toggles: { payments_enabled: false }, visible_tiers: {} }));
  }, []);

  const handleSignUp = () => {
    if (user) navigate('/home');
    else navigate('/login');
  };

  const tiers = settings?.visible_tiers || {};
  const paymentsOn = settings?.feature_toggles?.payments_enabled;
  const paymentCfg = settings?.payment_config || {};
  const lifetimeOffer = {
    enabled: !!paymentCfg.lifetime_offer_enabled,
    url: paymentCfg.lifetime_offer_url || '',
    label: paymentCfg.lifetime_offer_label || '₹5,999 - Lifetime Offer',
    note: paymentCfg.lifetime_offer_note || 'Independence Day Offer · Available for the first 499 students only — Grab The Offer Today!',
    tier: paymentCfg.lifetime_offer_tier || 'premium',
  };
  const lifetimeOfferOn = lifetimeOffer.enabled;            // show the offer button
  const lifetimeOfferReady = lifetimeOffer.enabled && !!lifetimeOffer.url;  // clickable
  const growthLink = {
    enabled: !!paymentCfg.growth_subscription_enabled,
    url: paymentCfg.growth_subscription_url || '',
    label: paymentCfg.growth_subscription_label || '',
  };
  const growthLinkOn = growthLink.enabled && !!growthLink.url;   // Get Growth → Razorpay link
  const eliteDisplayPrice = paymentCfg.elite_display_price_inr ?? 999;
  const eliteDisplayPeriod = paymentCfg.elite_display_period || 'month';
  const mkt = settings?.marketing_content || {};
  const pricingHeadingPrefix = mkt.pricing_heading_prefix || 'Start Learning Free. Upgrade';
  const pricingHeadingHighlight = mkt.pricing_heading_highlight || 'only when you love it.';
  const pricingSubtitle = mkt.pricing_subtitle || 'Transparent. Student-friendly. Cancel anytime.';
  const pricingEyebrow = mkt.pricing_eyebrow || 'Pricing';
  const basicPriceLabel = mkt.basic_price_label || 'Free';
  const basicPriceSuffix = mkt.basic_price_suffix || '';
  const basicCtaLabel = mkt.basic_cta_label || 'Get My Personal Tutor';
  const paidCtaPrefix = mkt.paid_cta_prefix || 'Get';
  const paidSoonLabel = mkt.paid_soon_label || 'Launching soon';
  const trialLabel = paymentCfg.trial_end_label || '';
  const trialHeadline = paymentCfg.trial_headline || 'Free for Every Student';
  // One message everywhere: while the free-access window is live the trial copy
  // (driven by the single admin date) wins over the seasonal marketing footnote.
  const pricingFootnote = (paymentCfg.campaign_trial_active && paymentCfg.trial_banner_copy)
    || mkt.pricing_footnote
    || (trialLabel ? `Free for every student until ${trialLabel} — every feature, every subject. No credit card, no commitment.` : 'Every feature, every subject. No credit card, no commitment.');

  return (
    <div className="min-h-screen font-body text-slate-800 bg-[#FEFBF4]" data-testid="landing-page" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Ambient background mesh */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -left-40 w-[45rem] h-[45rem] rounded-full bg-gradient-to-br from-indigo-300/25 via-cyan-200/20 to-transparent blur-3xl animate-float-slow" />
        <div className="absolute top-96 -right-40 w-[40rem] h-[40rem] rounded-full bg-gradient-to-br from-amber-200/30 via-rose-200/15 to-transparent blur-3xl animate-float" />
        <div className="absolute bottom-0 left-1/3 w-[50rem] h-[50rem] rounded-full bg-gradient-to-br from-emerald-200/15 via-teal-200/10 to-transparent blur-3xl" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FEFBF4]/75 border-b border-slate-900/5" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between">
          <button
            type="button"
            onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center gap-2.5 rounded-lg hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-indigo-300"
            data-testid="landing-logo"
            aria-label="Go to Gurukul Veda home"
          >
            <img src={BRAND_LOGO} alt="Gurukul Veda" className="w-11 h-11 sm:w-12 sm:h-12 2xl:w-14 2xl:h-14 object-contain" />
            <div className="text-left">
              <p className="font-display text-[17px] font-semibold text-slate-900 leading-tight tracking-tight">Gurukul Veda</p>
              <p className="text-[10px] text-slate-500 leading-tight font-body -mt-0.5">Your Personal Study Companion</p>
            </div>
          </button>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-600 font-body">
            <a href="#buddies" className="hover:text-slate-900 transition-colors">Buddies</a>
            <a href="#features" className="hover:text-slate-900 transition-colors">Features</a>
            <a href="#how" className="hover:text-slate-900 transition-colors">How it works</a>
            <a href="#pricing" className="hover:text-slate-900 transition-colors">Pricing</a>
            <a href="#story" className="hover:text-slate-900 transition-colors">Our story</a>
          </nav>
          <button onClick={handleSignUp} data-testid="landing-signup-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-heading hover:bg-slate-800 transition-all shadow-sm hover:shadow-md">
            {user ? 'Go to App' : 'Sign up · it\'s free'}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </header>

      {/* ============ FREE-ACCESS RIBBON ============
          Shown only while Admin → Settings → Free Trial has the banner
          switched on, so the message can be retired without touching dates. */}
      {paymentCfg.trial_banner_enabled !== false && (
      <div
        data-testid="beta-banner-landing"
        className="w-full bg-gradient-to-r from-amber-100 via-orange-100 to-rose-100 border-y border-amber-200/60"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-2.5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 text-center">
          <span className="inline-flex items-center gap-1.5 text-[11px] sm:text-sm font-heading font-extrabold uppercase tracking-widest text-amber-900 bg-white/70 backdrop-blur px-3 py-1 rounded-full border border-amber-300 shadow-sm">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75 animate-ping" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-rose-500" />
            </span>
            {trialHeadline}
          </span>
          <p className="font-body text-xs sm:text-sm text-amber-900 leading-tight">
            Get full access to Gurukul Veda — every feature, every subject, every learning tool. <span className="font-semibold">No credit card • No commitment • Start today.</span>
          </p>
        </div>
      </div>
      )}
      {/* ============ WEBSITE ADS CAROUSEL (admin-managed, top of page) ============ */}
      <WebsiteAdsCarousel onCta={handleSignUp} />

      {/* ============ HERO ============ */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-20 grid lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          <div className="text-left">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 backdrop-blur border border-emerald-100 text-emerald-700 text-xs font-heading mb-6 shadow-sm" data-testid="hero-pill">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              Built for CBSE · ICSE · IB · State Boards
            </div>

            <h1 className="font-display text-5xl sm:text-6xl lg:text-[72px] font-extrabold text-slate-900 leading-[1.02] tracking-[-0.035em]">
              The <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">Personal</span> tutor every Indian student
              <span className="block mt-1 bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent">secretly wished for.</span>
            </h1>

            <p className="mt-7 text-lg text-slate-600 max-w-xl leading-[1.65] font-body">
              Non-judgemental. Infinitely patient. Emotionally aware. <br className="hidden sm:inline" />
              Ask anything — from <span className="font-semibold text-slate-800">Pythagoras</span> to <span className="font-semibold text-slate-800">Dr. A.P.J. Abdul Kalam</span> — and get a warm, personalised explanation in your board's curriculum. Quizzes, flashcards, voice tutors, illustrated mini-lessons — all in one warm room.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <button onClick={handleSignUp} data-testid="hero-cta-primary"
                className="group btn-shimmer inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-slate-900 text-white text-sm font-heading font-semibold hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/10 hover:shadow-xl hover:shadow-slate-900/20">
                Start learning — it's free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
              <a href="#features" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/80 backdrop-blur border border-slate-200 text-slate-700 text-sm font-heading hover:bg-white hover:border-slate-300 transition-all">
                See what's inside
              </a>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-5 text-[12px] text-slate-500 font-body">
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> No credit card</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> Google sign-up in 10 seconds</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-500" /> 2026-27 NCERT aligned</span>
            </div>

            {/* Social proof row */}
            <div className="mt-10 flex items-center gap-4">
              <div className="flex -space-x-2">
                {['from-rose-400 to-pink-500', 'from-amber-400 to-orange-500', 'from-emerald-400 to-teal-500', 'from-indigo-400 to-blue-500'].map((g, i) => (
                  <div key={i} className={`w-8 h-8 rounded-full bg-gradient-to-br ${g} ring-2 ring-[#FEFBF4] flex items-center justify-center text-white font-heading text-[10px] font-bold`}>
                    {['A', 'P', 'S', 'R'][i]}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-0.5 text-amber-400">
                  {[0,1,2,3,4].map(i => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}
                </div>
                <p className="text-[11px] text-slate-500 mt-0.5 font-body">Loved by families across CBSE, ICSE & IB schools</p>
              </div>
            </div>
          </div>

          {/* Right: live chat preview */}
          <div className="relative">
            <HeroBrandPanel />
          </div>
        </div>
      </section>

      {/* ============ NON-JUDGMENT CHAT SHOWCASE ============ */}
      <section className="py-24 relative" data-testid="warmth-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-[1fr_1.25fr] gap-12 items-center">
          <Reveal>
            <p className="font-heading text-[11px] uppercase tracking-[0.3em] text-indigo-600 mb-3">A tutor who never makes you feel small</p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 leading-[1.05] tracking-[-0.025em]">
              Ask the <span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">silliest doubt</span>. Type the <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">worst typo</span>.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-[1.7]">
              We've designed every reply to sit beside your child — never above them. No sighs, no red underlines, no "you should already know this." Just warmth, clarity, and the confidence that your child's next question will always be met the same way: with a smile.
            </p>
            <div className="mt-7 grid grid-cols-2 gap-3 max-w-md">
              {[
                { icon: Heart, label: 'Zero judgement', color: 'text-rose-500' },
                { icon: Sparkles, label: 'Warm replies', color: 'text-amber-500' },
                { icon: Shield, label: 'Typo-friendly', color: 'text-emerald-500' },
                { icon: BookOpen, label: 'Anchored to syllabus', color: 'text-indigo-500' },
              ].map((p, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-slate-600">
                  <p.icon className={`w-4 h-4 ${p.color}`} />
                  <span className="font-body">{p.label}</span>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Mock chat preview */}
          <Reveal delay={150}>
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-indigo-200/40 via-cyan-200/30 to-amber-200/25 blur-3xl rounded-[3rem]" />
              <div className="relative card-3d rounded-[2rem] bg-white/90 backdrop-blur-xl border border-white shadow-2xl p-5 sm:p-6 overflow-hidden">
                {/* Tutor header */}
                <div className="flex items-center gap-3 pb-3.5 border-b border-slate-100">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 via-orange-400 to-rose-400 flex items-center justify-center text-white font-display font-bold text-sm">GV</div>
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-sm font-bold text-slate-900">Your Gurukul Veda tutor</p>
                    <p className="text-[11px] text-slate-500">Online · Usually replies in 3 seconds</p>
                  </div>
                  <span className="text-[9px] font-heading text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full uppercase tracking-wider">Live</span>
                </div>

                {/* Chat — Silly Doubt */}
                <div className="mt-4 space-y-3">
                  <div className="flex justify-end">
                    <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-br-md bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-[13px] shadow-sm font-body">
                      is air have color?? or its just empty??
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="max-w-[88%] px-3.5 py-3 rounded-2xl rounded-bl-md bg-slate-50 border border-slate-100 text-slate-800 text-[13px] leading-relaxed font-body">
                      <span className="font-semibold text-indigo-600">Brilliant question!</span> 🌬️ Air is invisible, not empty. It's actually packed with tiny molecules — mostly nitrogen and oxygen — that we can't see but we *can* feel. If air had colour, the sky might look very different!
                    </div>
                  </div>

                  {/* Micro badges — student's win */}
                  <div className="flex items-center gap-2 pl-2">
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 text-[10px] font-heading">
                      <Sparkles className="w-2.5 h-2.5" /> Aha!
                    </div>
                    <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 text-[10px] font-heading">
                      <Heart className="w-2.5 h-2.5" /> +5 Confidence
                    </div>
                  </div>

                  {/* Typo moment */}
                  <div className="flex justify-end pt-2">
                    <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-br-md bg-gradient-to-br from-indigo-500 to-blue-600 text-white text-[13px] shadow-sm font-body">
                      waht is fotosynthesiss 😔
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="max-w-[88%] px-3.5 py-3 rounded-2xl rounded-bl-md bg-slate-50 border border-slate-100 text-slate-800 text-[13px] leading-relaxed font-body">
                      Totally got you — you mean <span className="font-semibold text-slate-900">photosynthesis</span> 🌱 No stress on the spelling. It's how plants cook their own food using sunlight, water, and carbon dioxide. Imagine a tiny solar kitchen inside every leaf…
                    </div>
                  </div>
                </div>

                {/* Composer */}
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-2">
                  <div className="flex-1 px-3 py-2 rounded-xl bg-slate-50 text-[11px] text-slate-400">Ask anything — typos, silly doubts, even just "I'm stuck"…</div>
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-emerald-400 flex items-center justify-center text-white"><Camera className="w-3.5 h-3.5" /></div>
                  <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-sky-500 to-blue-500 flex items-center justify-center text-white"><Mic className="w-3.5 h-3.5" /></div>
                </div>
              </div>

              {/* Floating sticker */}
              <div className="hidden lg:block absolute -bottom-4 -left-4 bg-white shadow-xl rounded-2xl px-3.5 py-2 border border-slate-100 animate-float">
                <div className="flex items-center gap-2">
                  <span className="text-base">💬</span>
                  <div>
                    <p className="font-heading text-[11px] font-bold text-slate-800">Never a "wrong" question</p>
                    <p className="text-[9px] text-slate-400">Only the next step</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ MEET YOUR BUDDIES ============ */}
      <MeetYourBuddiesSection />

      {/* ============ FEATURES ============ */}
      <section id="features" className="relative py-24" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-heading text-[11px] uppercase tracking-[0.3em] text-indigo-600 mb-3">What you get</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-[56px] font-bold text-slate-900 max-w-3xl leading-[1.05] tracking-[-0.025em]">
              Everything a private tutor does — <span className="text-slate-500 font-semibold">plus what they can't.</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Brain, title: 'Personal Chat Tutor', color: 'from-indigo-500 to-blue-500', desc: 'Emotionally aware. Understands your exact CBSE/ICSE/IB chapter. Speaks warmly — never like a textbook.' },
              { icon: Mic, title: 'Personal Voice Tutor', color: 'from-sky-500 to-cyan-500', desc: 'Speak in English or 11 Indian regional languages. Get answers in a natural, warm voice — like calling your favourite didi.' },
              { icon: Camera, title: 'Snap, Upload & Solve', color: 'from-emerald-500 to-teal-500', desc: 'Stuck on a worksheet? Click a photo — we read the question and walk you through, step by step.' },
              { icon: ImageIcon, title: 'Illustrated Mini-Lessons', color: 'from-amber-500 to-orange-500', desc: 'Beautifully illustrated visuals with a one-line hook, core idea, key points, real-life example and a memory trick.' },
              { icon: Trophy, title: 'Quizzes & Flashcards', color: 'from-fuchsia-500 to-pink-500', desc: 'Board-aware, chapter-accurate. Confetti for every win. Streaks, Veda Coins, Aha! moments — all tracked.' },
              { icon: Users, title: 'Parent Dashboard', color: 'from-violet-500 to-indigo-500', desc: 'Multi-child support. Weekly progress reports. Study time. Streaks. All in one calm place.' },
            ].map((f, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="group card-3d h-full p-7 rounded-3xl bg-white/70 backdrop-blur border border-slate-900/5 hover:border-slate-900/10 hover:shadow-xl hover:shadow-slate-900/5 transition-shadow duration-500"
                  data-testid={`feature-${i}`}>
                  <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${f.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                    <f.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-slate-900 mt-5 mb-2 tracking-tight">{f.title}</h3>
                  <p className="text-[14px] text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section id="how" className="py-24" data-testid="how-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-heading text-[11px] uppercase tracking-[0.3em] text-indigo-600 mb-3">How it works</p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 max-w-2xl leading-[1.05] tracking-[-0.025em]">
              Three steps to your first <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">"aha!"</span> moment.
            </h2>
          </Reveal>

          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {[
              { n: '01', title: 'Sign up with Google', desc: 'Tell us your board, class, and school. Takes 30 seconds. No credit card.', icon: Shield },
              { n: '02', title: 'Pick your subject & chapter', desc: "We pull your exact NCERT or ICSE chapter list. No endless searching. Just what you're studying this week.", icon: BookOpen },
              { n: '03', title: 'Ask anything — get warmth back', desc: 'Your tutor answers with empathy, diagrams, voice, or a quiz. Whatever clicks for you.', icon: Heart },
            ].map((s, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="card-3d relative p-7 rounded-3xl bg-white/70 backdrop-blur border border-slate-900/5 shadow-sm hover:shadow-lg transition-shadow h-full" data-testid={`step-${i}`}>
                  <p className="font-display text-6xl font-bold bg-gradient-to-br from-indigo-400 to-blue-600 bg-clip-text text-transparent leading-none tracking-[-0.03em]">{s.n}</p>
                  <div className="w-11 h-11 rounded-2xl bg-indigo-50 flex items-center justify-center mt-5 mb-3">
                    <s.icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-slate-900 mb-2 tracking-tight">{s.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIAL / STORY ============ */}
      <section id="story" className="py-24">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="font-heading text-[11px] uppercase tracking-[0.3em] text-indigo-600 mb-3">Why parents trust us</p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 leading-[1.05] tracking-[-0.025em] mb-6">
              Built with the same care <br /><span className="bg-gradient-to-r from-indigo-600 to-blue-600 bg-clip-text text-transparent">you'd want for your own child.</span>
            </h2>
            <p className="text-slate-600 leading-[1.7]">
              We started Gurukul Veda because we watched bright children lose confidence in the gap between *asking* and *understanding*. Every reply, every illustration, every streak — crafted with the warmth of a mentor who believes in your child before they do.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                { icon: Shield, title: 'Private', desc: 'No ads. No data sale.' },
                { icon: Heart, title: 'Patient', desc: 'Zero judgement. Ever.' },
                { icon: BookOpen, title: 'Accurate', desc: '2026-27 NCERT.' },
              ].map((t, i) => (
                <div key={i} className="p-4 rounded-2xl bg-[#FEFBF4] border border-slate-900/5">
                  <t.icon className="w-4 h-4 text-indigo-600 mb-2" />
                  <p className="font-display text-sm font-medium text-slate-900">{t.title}</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">{t.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Quote card */}
          <Reveal delay={150}>
            <div className="relative">
              <div className="absolute -inset-6 bg-gradient-to-br from-amber-200/30 via-rose-200/20 to-indigo-200/20 blur-2xl rounded-[3rem]" />
              <div className="relative p-8 sm:p-10 rounded-[2rem] bg-gradient-to-br from-amber-50 via-orange-50/80 to-rose-50 border border-amber-100/80 shadow-xl">
                <Quote className="w-10 h-10 text-amber-400 mb-4" strokeWidth={1.5} />
                <p className="text-[22px] sm:text-[26px] leading-[1.45] text-slate-800 font-medium font-body">
                  "My daughter went from hating maths to voluntarily opening her tablet after dinner. It doesn't feel like an app — it feels like someone is <span className="bg-gradient-to-r from-amber-600 to-orange-600 bg-clip-text text-transparent font-bold">actually listening</span>."
                </p>
                <div className="mt-7 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-rose-400 flex items-center justify-center text-white font-display font-bold shadow">P</div>
                  <div>
                    <p className="font-heading text-sm font-bold text-slate-900">Priya Raghavan</p>
                    <p className="text-[11px] text-slate-500">Parent · Class 8 · DPS Bangalore</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section id="pricing" className="py-24" data-testid="pricing-section">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-14">
              <p className="font-heading text-[11px] uppercase tracking-[0.3em] text-indigo-600 mb-3" data-testid="pricing-eyebrow">{pricingEyebrow}</p>
              <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 leading-[1.05] tracking-[-0.025em]" data-testid="pricing-heading">
                {pricingHeadingPrefix} <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">{pricingHeadingHighlight}</span>
              </h2>
              <p className="mt-4 text-slate-500" data-testid="pricing-subtitle">{pricingSubtitle}</p>
            </div>
          </Reveal>

          <div className={`grid gap-5 ${Object.keys(tiers).length === 1 ? 'grid-cols-1 max-w-md mx-auto' : Object.keys(tiers).length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto' : 'grid-cols-1 md:grid-cols-3'}`}>
            {Object.entries(tiers).map(([key, tier], idx) => {
              const meta = {
                basic: { icon: Zap, ring: 'ring-slate-200', accent: 'from-slate-700 to-slate-900', halo: 'from-slate-200/0' },
                growth: { icon: Star, ring: 'ring-indigo-300', accent: 'from-indigo-600 to-blue-600', halo: 'from-indigo-200/40' },
                premium: { icon: Crown, ring: 'ring-amber-300', accent: 'from-amber-500 to-orange-500', halo: 'from-amber-200/40' },
                elite: { icon: Crown, ring: 'ring-purple-300', accent: 'from-purple-500 to-fuchsia-500', halo: 'from-purple-200/40' },
              }[key] || { icon: Zap, ring: 'ring-slate-200', accent: 'from-slate-700 to-slate-900', halo: 'from-slate-200/0' };
              const TierIcon = meta.icon;
              const isFree = (tier.price || 0) === 0;
              const isFeatured = key === 'growth';
              const isLifetimeTier = key === lifetimeOffer.tier;
              return (
                <Reveal key={key} delay={idx * 80}>
                  <div data-testid={`tier-${key}`}
                    className={`relative h-full p-7 rounded-3xl bg-white ring-1 ${meta.ring} ${isFeatured ? 'ring-2 shadow-2xl shadow-indigo-500/10 md:scale-[1.02]' : 'shadow-sm'} transition-all hover:shadow-lg`}>
                    <div className={`absolute -inset-px rounded-3xl bg-gradient-to-br ${meta.halo} to-transparent pointer-events-none ${isFeatured ? 'opacity-100' : 'opacity-0'}`} />
                    {isFeatured && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white text-[10px] font-heading font-semibold uppercase tracking-wider shadow">Most loved</span>
                    )}
                    <div className="relative">
                      <div className="flex items-center gap-3 mb-5">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.accent} flex items-center justify-center shadow`}>
                          <TierIcon className="w-4.5 h-4.5 text-white" />
                        </div>
                        <h3 className="font-display text-2xl font-bold text-slate-900 tracking-tight">{tier.name}</h3>
                      </div>
                      <div className="mb-6">
                        {isFree ? (
                          <p className="font-display text-4xl font-bold text-slate-900 tracking-[-0.02em]" data-testid={`tier-price-${key}`}>
                            {basicPriceLabel}
                            {basicPriceSuffix ? <span className="text-base text-slate-400 font-body font-medium"> {basicPriceSuffix}</span> : null}
                          </p>
                        ) : (
                          <p className="font-display text-4xl font-bold text-slate-900 tracking-[-0.02em]" data-testid={`tier-price-${key}`}>₹{isLifetimeTier ? eliteDisplayPrice : tier.price}<span className="text-base text-slate-400 font-body font-medium"> /{isLifetimeTier ? eliteDisplayPeriod : 'month'}</span></p>
                        )}
                      </div>
                      <ul className="space-y-2.5 mb-7">
                        {(tier.features || []).slice(0, 7).map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      {(() => {
                        const growthLinkHere = key === 'growth' && growthLinkOn;
                        const clickHandler =
                          isLifetimeTier && lifetimeOfferReady ? () => window.open(lifetimeOffer.url, '_blank', 'noopener,noreferrer') :
                          growthLinkHere ? () => window.open(growthLink.url, '_blank', 'noopener,noreferrer') :
                          handleSignUp;
                        const isDisabled = isLifetimeTier ? !lifetimeOfferReady : (!isFree && !growthLinkHere && !paymentsOn);
                        const label =
                          isFree ? basicCtaLabel :
                          isLifetimeTier ? (lifetimeOfferOn ? (lifetimeOffer.label || '₹5,999 - Lifetime Offer') : paidSoonLabel) :
                          growthLinkHere ? (growthLink.label || `${paidCtaPrefix} ${tier.name}`) :
                          paymentsOn ? `${paidCtaPrefix} ${tier.name}` : paidSoonLabel;
                        const cls =
                          isFree ? 'bg-slate-900 text-white hover:bg-slate-800' :
                          isLifetimeTier ? (lifetimeOfferOn ? `bg-gradient-to-r from-purple-500 to-fuchsia-500 text-white hover:shadow-lg ${lifetimeOfferReady ? '' : 'opacity-60 cursor-not-allowed'}` : 'bg-slate-100 text-slate-400 cursor-default') :
                          (growthLinkHere || paymentsOn) ? `bg-gradient-to-r ${meta.accent} text-white hover:shadow-lg` :
                          'bg-slate-100 text-slate-400 cursor-default';
                        const testid = isLifetimeTier && lifetimeOfferReady ? 'tier-cta-lifetime' : growthLinkHere ? 'tier-cta-growth-link' : `tier-cta-${key}`;
                        return (
                          <button onClick={clickHandler} disabled={isDisabled} data-testid={testid}
                            className={`w-full py-3 px-4 rounded-3xl font-heading text-sm leading-snug whitespace-normal min-h-[48px] transition-all ${cls}`}>
                            {label}
                          </button>
                        );
                      })()}
                      {isLifetimeTier && lifetimeOfferOn && (
                        <p data-testid="lifetime-offer-note" className="mt-3 text-center text-[12.5px] leading-snug font-heading text-purple-700">
                          {lifetimeOffer.note}
                        </p>
                      )}
                    </div>
                  </div>
                </Reveal>
              );
            })}
            {Object.keys(tiers).length === 0 && (
              <div className="md:col-span-3 text-center py-12 text-slate-400 text-sm">Plans loading…</div>
            )}
          </div>
          {!paymentsOn && Object.keys(tiers).length > 1 && (
            <p className="text-center text-xs text-slate-400 mt-6" data-testid="payments-off-notice">
              {pricingFootnote}
            </p>
          )}
        </div>
      </section>

      {/* ============ BIG CTA — stays on cream palette, visual continuity ============ */}
      <section className="relative py-28 overflow-hidden bg-[#FEFBF4]" data-testid="bigcta-section">
        {/* Soft radial accents — same palette family as hero */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-1/4 w-[30rem] h-[30rem] rounded-full bg-gradient-to-br from-indigo-200/40 to-transparent blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-[30rem] h-[30rem] rounded-full bg-gradient-to-br from-amber-200/40 to-transparent blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
          <div className="relative rounded-[2.5rem] bg-white/90 backdrop-blur-xl border border-slate-900/5 shadow-[0_20px_80px_-20px_rgba(30,41,59,0.25)] px-8 sm:px-16 py-16 sm:py-20 text-center overflow-hidden">
            {/* Decorative glow inside card */}
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[40rem] h-[20rem] rounded-full bg-gradient-to-b from-indigo-200/40 to-transparent blur-3xl pointer-events-none" />
            <div className="relative">
              <img src={BRAND_LOGO} alt="" className="w-16 h-16 mx-auto mb-6" />
              <h2 className="font-display text-4xl sm:text-5xl lg:text-[56px] font-bold leading-[1.05] tracking-[-0.025em] text-slate-900 max-w-2xl mx-auto">
                Your child's next <span className="bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 bg-clip-text text-transparent">"aha!"</span> is a login away.
              </h2>
              <p className="mt-5 text-slate-600 text-lg max-w-xl mx-auto leading-relaxed">
                Join the families who already found their favourite study companion.
              </p>
              <button onClick={handleSignUp} data-testid="bigcta-btn"
                className="btn-shimmer mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-slate-900 text-white text-sm font-heading font-semibold shadow-xl shadow-slate-900/20 hover:bg-slate-800 hover:scale-[1.02] transition-all">
                Sign up with Google · it's free
                <ArrowRight className="w-4 h-4" />
              </button>
              <p className="mt-4 text-[12px] text-slate-400">Takes 10 seconds · No credit card · Cancel anytime</p>
            </div>
          </div>
        </div>
      </section>

      <ProductTour />

      {/* ============ FOOTER ============ */}
      <footer className="py-12 border-t border-slate-900/5 bg-[#FEFBF4]" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <img src={BRAND_LOGO} alt="" className="w-8 h-8" />
              <div>
                <p className="font-display text-base font-semibold text-slate-900 tracking-tight">Gurukul Veda</p>
                <p className="text-[11px] text-slate-400">Your Personal Study Companion</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-xs text-slate-500 font-body">
              <a href="#features" className="hover:text-slate-800">Features</a>
              <a href="#pricing" className="hover:text-slate-800">Pricing</a>
              <a href="/learn" className="hover:text-slate-800" data-testid="footer-learn-hub">Browse subjects</a>
              <a href="#story" className="hover:text-slate-800">Our story</a>
              <a href="mailto:hello@gurukulveda.com" className="hover:text-slate-800">hello@gurukulveda.com</a>
            </div>
          </div>
          {/* Legal row — required by payment gateways */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-5 border-t border-slate-100">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-slate-400 font-body" data-testid="landing-legal-row">
              <a href="/terms" className="hover:text-slate-700" data-testid="landing-footer-terms">Terms & Conditions</a>
              <a href="/privacy" className="hover:text-slate-700" data-testid="landing-footer-privacy">Privacy Policy</a>
              <a href="/refund" className="hover:text-slate-700" data-testid="landing-footer-refund">Refund & Cancellation</a>
              <a href="/shipping" className="hover:text-slate-700" data-testid="landing-footer-shipping">Shipping & Delivery</a>
              <a href="/contact" className="hover:text-slate-700" data-testid="landing-footer-contact">Contact Us</a>
            </div>
            <p className="text-[11px] text-slate-400 font-body">© {year} Gurukul Veda · Made with <Heart className="w-3 h-3 inline text-rose-400 fill-current" /> for Indian students.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
